import * as THREE from "three";

type AnimationMode = "rotate" | "swipe";

export class WireframeCube {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private cube: THREE.Group = new THREE.Group();
  private animationId: number = 0;
  private container: HTMLElement;
  private width: number;
  private height: number;
  private animationMode: AnimationMode = "rotate";
  private subdivisions: number = 6;
  private swipeAnimationState: {
    isAnimating: boolean;
    startTime: number;
    startRotation: { x: number; y: number; z: number };
    targetRotation: { x: number; y: number; z: number };
    lastTurnTime: number;
  } = {
    isAnimating: false,
    startTime: 0,
    startRotation: { x: 0, y: 0, z: 0 },
    targetRotation: { x: 0, y: 0, z: 0 },
    lastTurnTime: 0,
  };

  private userControlState: {
    isActive: boolean;
    lastMouseX: number;
    lastMouseY: number;
    lastTouchX: number;
    lastTouchY: number;
    velocityX: number;
    velocityY: number;
    momentumDecay: number;
    isMouseOver: boolean;
    isTouching: boolean;
  } = {
    isActive: false,
    lastMouseX: 0,
    lastMouseY: 0,
    lastTouchX: 0,
    lastTouchY: 0,
    velocityX: 0,
    velocityY: 0,
    momentumDecay: 0.95,
    isMouseOver: false,
    isTouching: false,
  };

  constructor(container: HTMLElement) {
    this.container = container;
    this.width = container.clientWidth;
    this.height = container.clientHeight;

    const lineWidth = container.getAttribute("cube-line-width");
    if (lineWidth) {
      this.setLineWidth(parseInt(lineWidth));
    }

    const animationMode = container.getAttribute(
      "cube-animation-mode"
    ) as AnimationMode;
    if (
      animationMode &&
      (animationMode === "rotate" || animationMode === "swipe")
    ) {
      this.animationMode = animationMode;
    }

    const subdivisions = container.getAttribute("cube-subdivisions");
    if (subdivisions) {
      const parsedSubdivisions = parseInt(subdivisions);
      if (parsedSubdivisions > 0 && parsedSubdivisions <= 20) {
        this.subdivisions = parsedSubdivisions;
      }
    }

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.width / this.height,
      0.1,
      1000
    );
    this.renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: "high-performance",
    });

    this.setupRenderer();
    this.createCube(container);
    this.setupLighting();
    this.setupCamera();
    this.setupEventListeners();
    this.animate();
    this.handleResize();
  }

  private setupRenderer(): void {
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.container.appendChild(this.renderer.domElement);
  }

  private createCube(container: HTMLElement): void {
    this.cube = new THREE.Group();

    const geometry = new THREE.BoxGeometry(3, 3, 3);

    let lineWidth = 1;
    const lineWidthAttr = container.getAttribute("cube-line-width");
    if (lineWidthAttr) {
      lineWidth = parseInt(lineWidthAttr);
    }

    // Create subdivision lines texture
    const lineTexture = this.createSubdivisionTexture({
      lineWidth,
      subdivisions: this.subdivisions,
    });

    // Create transparent material with subdivision lines
    const cubeMaterial = new THREE.MeshBasicMaterial({
      map: lineTexture,
      transparent: true,
      opacity: 1.0, // Increased opacity for clearer lines
      side: THREE.FrontSide, // Enable backface culling
      alphaTest: 0.01, // Lower alpha test for better line visibility
      depthWrite: false, // Disable depth writing for better transparency
    });

    // Create the solid cube mesh
    const cubeMesh = new THREE.Mesh(geometry, cubeMaterial);
    this.cube.add(cubeMesh);

    // Set initial position based on animation mode
    if (this.animationMode === "swipe") {
      // 45 degrees sideways and 30 degrees tipped towards camera
      this.cube.rotation.y = Math.PI / 4; // 45 degrees
      this.cube.rotation.x = Math.PI / 6; // 30 degrees
    }

    this.scene.add(this.cube);
  }

  private createSubdivisionTexture({
    color = "#5FFFFB",
    lineWidth = 1,
    subdivisions = 6,
  }: {
    color?: string;
    lineWidth?: number;
    subdivisions?: number;
  } = {}): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    const size = 1024; // Increased resolution for crisp lines
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d")!;

    // Enable crisp rendering
    ctx.imageSmoothingEnabled = false;

    // Clear canvas with transparent background
    ctx.clearRect(0, 0, size, size);

    // Set line properties for crisp rendering
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, lineWidth * 2); // Scale line width for higher resolution
    ctx.lineCap = "square"; // Use square caps for cleaner lines
    ctx.lineJoin = "miter";

    // Draw subdivision lines
    const step = size / subdivisions;

    // Draw vertical lines with pixel-perfect positioning
    for (let i = 0; i <= subdivisions; i++) {
      const x = Math.round(i * step) + 0.5; // Add 0.5 for crisp 1px lines
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, size);
      ctx.stroke();
    }

    // Draw horizontal lines with pixel-perfect positioning
    for (let i = 0; i <= subdivisions; i++) {
      const y = Math.round(i * step) + 0.5; // Add 0.5 for crisp 1px lines
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(size, y);
      ctx.stroke();
    }

    // Create texture from canvas with optimal settings for crisp rendering
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false; // Disable mipmaps for crisp lines
    texture.anisotropy = 1; // Disable anisotropy for pixel-perfect rendering

    return texture;
  }

  private setupLighting(): void {
    // Ambient light for overall illumination
    const ambientLight = new THREE.AmbientLight(0x404040, 0.3);
    this.scene.add(ambientLight);

    // Directional light for depth
    const directionalLight = new THREE.DirectionalLight(0x5ffffb, 0.5);
    directionalLight.position.set(5, 5, 5);
    this.scene.add(directionalLight);

    // Point light for glow effect
    const pointLight = new THREE.PointLight(0x5ffffb, 0.8, 100);
    pointLight.position.set(0, 0, 0);
    this.scene.add(pointLight);
  }

  private setupCamera(): void {
    // Adjust camera distance based on container size
    const cameraDistance = this.width < 200 ? 4 : 6;
    this.camera.position.set(0, 0, cameraDistance);
    this.camera.lookAt(0, 0, 0);
  }

  private setupEventListeners(): void {
    // Mouse events
    this.container.addEventListener(
      "mouseenter",
      this.handleMouseEnter.bind(this)
    );
    this.container.addEventListener(
      "mouseleave",
      this.handleMouseLeave.bind(this)
    );
    this.container.addEventListener(
      "mousemove",
      this.handleMouseMove.bind(this)
    );

    // Touch events
    this.container.addEventListener(
      "touchstart",
      this.handleTouchStart.bind(this),
      { passive: false }
    );
    this.container.addEventListener(
      "touchmove",
      this.handleTouchMove.bind(this),
      { passive: false }
    );
    this.container.addEventListener(
      "touchend",
      this.handleTouchEnd.bind(this),
      { passive: false }
    );
  }

  private animate(): void {
    this.animationId = requestAnimationFrame(() => this.animate());

    if (this.userControlState.isActive) {
      this.animateUserControl();
    } else if (this.animationMode === "rotate") {
      this.animateRotate();
    } else if (this.animationMode === "swipe") {
      this.animateSwipe();
    }

    this.renderer.render(this.scene, this.camera);
  }

  private animateRotate(): void {
    // Rotate the single cube slowly and smoothly
    this.cube.rotation.x += 0.002;
    this.cube.rotation.y += 0.003;
    this.cube.rotation.z += 0.001;
  }

  private animateSwipe(): void {
    const currentTime = Date.now();

    // Check if it's time for a new turn (every 3 seconds)
    if (currentTime - this.swipeAnimationState.lastTurnTime >= 2000) {
      this.startSwipeTurn();
    }

    // If currently animating, update the rotation
    if (this.swipeAnimationState.isAnimating) {
      this.updateSwipeAnimation();
    }
  }

  private animateUserControl(): void {
    if (this.userControlState.isTouching) {
      // Apply momentum for touch interactions
      this.cube.rotation.y += this.userControlState.velocityX * 0.01;
      this.cube.rotation.x += this.userControlState.velocityY * 0.01;

      // Apply momentum decay
      this.userControlState.velocityX *= this.userControlState.momentumDecay;
      this.userControlState.velocityY *= this.userControlState.momentumDecay;

      // Stop momentum when it's very small
      if (
        Math.abs(this.userControlState.velocityX) < 0.001 &&
        Math.abs(this.userControlState.velocityY) < 0.001
      ) {
        this.userControlState.velocityX = 0;
        this.userControlState.velocityY = 0;
      }
    }
  }

  private startSwipeTurn(): void {
    const currentTime = Date.now();

    // Set new target rotation (90 degrees in random direction)
    const directions = [
      { x: Math.PI / 2, y: 0, z: 0 }, // 90° around X
      { x: 0, y: Math.PI / 2, z: 0 }, // 90° around Y
      { x: 0, y: 0, z: Math.PI / 2 }, // 90° around Z
      { x: Math.PI / 2, y: Math.PI / 2, z: 0 }, // 90° around X and Y
      { x: 0, y: Math.PI / 2, z: Math.PI / 2 }, // 90° around Y and Z
      { x: Math.PI / 2, y: 0, z: Math.PI / 2 }, // 90° around X and Z
    ];

    const randomDirection =
      directions[Math.floor(Math.random() * directions.length)];

    this.swipeAnimationState.startRotation = {
      x: this.cube.rotation.x,
      y: this.cube.rotation.y,
      z: this.cube.rotation.z,
    };

    this.swipeAnimationState.targetRotation = {
      x: this.swipeAnimationState.startRotation.x + randomDirection.x,
      y: this.swipeAnimationState.startRotation.y + randomDirection.y,
      z: this.swipeAnimationState.startRotation.z + randomDirection.z,
    };

    this.swipeAnimationState.startTime = currentTime;
    this.swipeAnimationState.isAnimating = true;
    this.swipeAnimationState.lastTurnTime = currentTime;
  }

  private updateSwipeAnimation(): void {
    const currentTime = Date.now();
    const elapsed = currentTime - this.swipeAnimationState.startTime;
    const duration = 500; // 500ms animation duration

    if (elapsed >= duration) {
      // Animation complete
      this.cube.rotation.x = this.swipeAnimationState.targetRotation.x;
      this.cube.rotation.y = this.swipeAnimationState.targetRotation.y;
      this.cube.rotation.z = this.swipeAnimationState.targetRotation.z;
      this.swipeAnimationState.isAnimating = false;
    } else {
      // Interpolate between start and target rotation
      const progress = elapsed / duration;
      const easeProgress = this.easeInOutCubic(progress);

      this.cube.rotation.x = this.lerp(
        this.swipeAnimationState.startRotation.x,
        this.swipeAnimationState.targetRotation.x,
        easeProgress
      );
      this.cube.rotation.y = this.lerp(
        this.swipeAnimationState.startRotation.y,
        this.swipeAnimationState.targetRotation.y,
        easeProgress
      );
      this.cube.rotation.z = this.lerp(
        this.swipeAnimationState.startRotation.z,
        this.swipeAnimationState.targetRotation.z,
        easeProgress
      );
    }
  }

  private lerp(start: number, end: number, factor: number): number {
    return start + (end - start) * factor;
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  private handleMouseEnter(event: MouseEvent): void {
    this.userControlState.isMouseOver = true;
    this.userControlState.isActive = true;
    this.userControlState.lastMouseX = event.clientX;
    this.userControlState.lastMouseY = event.clientY;
  }

  private handleMouseLeave(_event: MouseEvent): void {
    this.userControlState.isMouseOver = false;
    if (!this.userControlState.isTouching) {
      this.userControlState.isActive = false;
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    if (!this.userControlState.isMouseOver) return;

    const deltaX = event.clientX - this.userControlState.lastMouseX;
    const deltaY = event.clientY - this.userControlState.lastMouseY;

    // Apply rotation based on mouse movement
    this.cube.rotation.y += deltaX * 0.01;
    this.cube.rotation.x += deltaY * 0.01;

    this.userControlState.lastMouseX = event.clientX;
    this.userControlState.lastMouseY = event.clientY;
  }

  private handleTouchStart(event: TouchEvent): void {
    event.preventDefault();
    this.userControlState.isTouching = true;
    this.userControlState.isActive = true;

    if (event.touches.length > 0) {
      this.userControlState.lastTouchX = event.touches[0].clientX;
      this.userControlState.lastTouchY = event.touches[0].clientY;
      this.userControlState.velocityX = 0;
      this.userControlState.velocityY = 0;
    }
  }

  private handleTouchMove(event: TouchEvent): void {
    if (!this.userControlState.isTouching || event.touches.length === 0) return;

    event.preventDefault();

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;

    const deltaX = currentX - this.userControlState.lastTouchX;
    const deltaY = currentY - this.userControlState.lastTouchY;

    // Calculate velocity for momentum
    this.userControlState.velocityX = deltaX * 0.5;
    this.userControlState.velocityY = deltaY * 0.5;

    // Apply immediate rotation
    this.cube.rotation.y += deltaX * 0.01;
    this.cube.rotation.x += deltaY * 0.01;

    this.userControlState.lastTouchX = currentX;
    this.userControlState.lastTouchY = currentY;
  }

  private handleTouchEnd(_event: TouchEvent): void {
    this.userControlState.isTouching = false;
    if (!this.userControlState.isMouseOver) {
      this.userControlState.isActive = false;
    }
  }

  private handleResize(): void {
    window.addEventListener("resize", () => {
      // Update dimensions from container
      this.width = this.container.clientWidth;
      this.height = this.container.clientHeight;

      this.camera.aspect = this.width / this.height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.width, this.height);
    });
  }

  public destroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    // Remove event listeners
    this.container.removeEventListener(
      "mouseenter",
      this.handleMouseEnter.bind(this)
    );
    this.container.removeEventListener(
      "mouseleave",
      this.handleMouseLeave.bind(this)
    );
    this.container.removeEventListener(
      "mousemove",
      this.handleMouseMove.bind(this)
    );
    this.container.removeEventListener(
      "touchstart",
      this.handleTouchStart.bind(this)
    );
    this.container.removeEventListener(
      "touchmove",
      this.handleTouchMove.bind(this)
    );
    this.container.removeEventListener(
      "touchend",
      this.handleTouchEnd.bind(this)
    );

    if (this.container && this.renderer.domElement) {
      this.container.removeChild(this.renderer.domElement);
    }

    this.renderer.dispose();
  }

  public setColor(color: string): void {
    this.cube.traverse((child) => {
      if (
        child instanceof THREE.Mesh &&
        child.material instanceof THREE.MeshBasicMaterial
      ) {
        // Update the texture color by recreating it
        const newTexture = this.createSubdivisionTexture({
          color,
          subdivisions: this.subdivisions,
        });
        child.material.map = newTexture;
      }
    });
  }

  public setLineWidth(width: number): void {
    // Line width is now controlled by the texture, so we need to recreate it
    this.cube.traverse((child) => {
      if (
        child instanceof THREE.Mesh &&
        child.material instanceof THREE.MeshBasicMaterial
      ) {
        const newTexture = this.createSubdivisionTexture({
          lineWidth: width,
          subdivisions: this.subdivisions,
        });
        child.material.map = newTexture;
      }
    });
  }
}
